import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, CheckCircle2, Circle, Clock, CalendarClock, ExternalLink, Pencil, Trash2, RotateCcw, ListTodo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDate, initials } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "today", label: "Today", accent: "#0057A8" },
  { key: "upcoming", label: "Upcoming", accent: "#0284C7" },
  { key: "overdue", label: "Overdue", accent: "#E31E24" },
  { key: "all", label: "All", accent: "#64748B" },
];

export default function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canWrite = user?.role !== "ACCOUNTS";
  const [tab, setTab] = useState(location.state?.tab || "today");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [reschedule, setReschedule] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [cur, all] = await Promise.all([
      api.get(`/tasks${tab === "all" ? "" : `?filter=${tab}`}`),
      api.get(`/tasks`),
    ]);
    setItems(cur.data.items);
    const list = all.data.items;
    const today = new Date().toISOString().slice(0, 10);
    setCounts({
      today: list.filter((t) => (t.due_date || "").slice(0, 10) === today && t.status !== "Completed").length,
      upcoming: list.filter((t) => (t.due_date || "").slice(0, 10) > today && t.status !== "Completed").length,
      overdue: list.filter((t) => t.status === "Overdue").length,
      all: list.length,
    });
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (location.state?.tab) setTab(location.state.tab); }, [location.state]);

  const complete = async (t) => { await api.patch(`/tasks/${t.id}/status`, { status: t.status === "Completed" ? "Pending" : "Completed" }); toast.success(t.status === "Completed" ? "Marked pending" : "Task completed"); load(); };
  const doReschedule = async (t, date) => { await api.put(`/tasks/${t.id}`, { ...t, due_date: date }); toast.success("Rescheduled"); setReschedule(null); load(); };
  const doDelete = async () => { await api.delete(`/tasks/${confirm.id}`); toast.success("Task deleted"); setConfirm(null); load(); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Tasks & Follow-ups</h1><p className="mt-0.5 text-sm text-slate-500">{canWrite ? "Stay on top of every follow-up" : "Tasks assigned to you"}</p></div>
        {canWrite && <Button data-testid="new-task-button" onClick={() => { setEdit(null); setFormOpen(true); }} className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> New Task</Button>}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tasks-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-semibold transition ${tab === t.key ? "border-transparent text-white shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
            style={tab === t.key ? { backgroundColor: t.accent } : {}}>
            {t.label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === t.key ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="tasks-empty" icon={ListTodo} title={tab === "overdue" ? "Nothing overdue" : "No tasks here"} description={tab === "overdue" ? "You're all caught up — great work!" : (canWrite ? "Create a task to schedule your next follow-up." : "No tasks assigned to you right now.")} action={canWrite ? <Button onClick={() => setFormOpen(true)} className="gap-1.5"><Plus className="h-4 w-4" /> New Task</Button> : null} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {items.map((t) => {
            const overdue = t.status === "Overdue";
            return (
              <div key={t.id} data-testid={`task-item-${t.id}`} className={`group flex items-center gap-3 border-b border-slate-50 px-4 py-3.5 transition hover:bg-slate-50/60 ${overdue ? "bg-red-50/30" : ""}`}>
                <button onClick={() => complete(t)} data-testid={`task-complete-${t.id}`}>{t.status === "Completed" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className={`h-5 w-5 ${overdue ? "text-red-400" : "text-slate-300"} hover:text-emerald-400`} />}</button>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium ${t.status === "Completed" ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
                    {t.company_name && <span>{t.company_name}</span>}
                    <span className={`flex items-center gap-1 ${overdue ? "font-semibold text-red-600" : ""}`}><CalendarClock className="h-3 w-3" />{formatDate(t.due_date)}</span>
                  </div>
                </div>
                <StatusBadge value={t.priority} type="priority" />
                <StatusBadge value={t.status} type="status" />
                <div className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
                  {t.lead_id && <button title="Open lead" onClick={() => navigate(`/leads/${t.lead_id}`)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><ExternalLink className="h-4 w-4" /></button>}
                  {canWrite && <Popover open={reschedule === t.id} onOpenChange={(o) => setReschedule(o ? t.id : null)}>
                    <PopoverTrigger asChild><button title="Reschedule" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><RotateCcw className="h-4 w-4" /></button></PopoverTrigger>
                    <PopoverContent className="w-56 p-3" align="end">
                      <label className="mb-1.5 block text-xs font-semibold text-slate-600">New due date</label>
                      <Input type="date" defaultValue={(t.due_date || "").slice(0, 10)} onChange={(e) => doReschedule(t, e.target.value)} />
                    </PopoverContent>
                  </Popover>}
                  {canWrite && <button title="Edit" onClick={() => { setEdit(t); setFormOpen(true); }} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
                  {canWrite && <button title="Delete" onClick={() => setConfirm(t)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TaskFormDialog open={formOpen} onOpenChange={setFormOpen} task={edit} onSaved={load} />
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title="Delete task?" description="This will permanently remove the task." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}
